/**
 * Sign in with Apple — the only auth surface in the native app.
 *
 * Build 190: receipt-first / device-UUID auto-auth removed from the cold
 * launch path. Splash → /auth/me → if no valid session, the router lands
 * here. One Apple button. No Google. No styling beyond what's needed to
 * render. Functional first.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { toast } from 'sonner';

import api, { setAuthToken, apiErrorMessage } from '@/lib/api';

const APPLE_SERVICE_ID =
  process.env.REACT_APP_APPLE_SERVICE_ID || 'com.smarterhoops.app.signin';
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  'https://app.smarterhoops.com/api/auth/apple/callback';

function randomNonce() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

export default function Login() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const handleApple = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const isNative = (() => {
        try { return Capacitor.isNativePlatform(); } catch { return false; }
      })();
      if (!isNative) {
        toast.error('Sign in with Apple only works on iOS.');
        return;
      }

      const nonce = randomNonce();
      const result = await SignInWithApple.authorize({
        clientId: APPLE_SERVICE_ID,
        redirectURI: APPLE_REDIRECT_URI,
        scopes: 'email name',
        nonce,
      });

      const r = result?.response || {};
      const identity_token = r.identityToken;
      const authorization_code = r.authorizationCode;
      const givenName = r.givenName;
      const familyName = r.familyName;
      if (!identity_token) {
        toast.error('Apple did not return an identity token.');
        return;
      }

      const { data } = await api.post('/auth/sign-in-with-apple', {
        identity_token,
        authorization_code,
        nonce,
        full_name:
          givenName || familyName
            ? { givenName: givenName || '', familyName: familyName || '' }
            : undefined,
        platform: 'ios',
      });

      if (data?.token) setAuthToken(data.token);
      navigate(data?.role === 'admin' ? '/admin/dashboard' : '/dashboard', {
        replace: true,
      });
    } catch (err) {
      // Cold-start (5xx / network) → friendly "warming up" copy via
      // apiErrorMessage. Real 4xx (e.g. token verification failed) keeps
      // the backend-supplied detail. User-cancelled native sheet → silent.
      const detail = apiErrorMessage(
        err,
        err?.message || 'Sign in with Apple failed'
      );
      console.error('[Login] Apple sign-in failed:', detail);
      // Apple's plugin throws a string code we can't reliably
      // structure-match, so we substring on the common cancel signals.
      const rawText = String(
        err?.response?.data?.detail || err?.message || ''
      ).toLowerCase();
      const cancelled =
        rawText.includes('cancel') ||
        rawText.includes('1001') ||
        rawText.includes('user denied');
      if (!cancelled) toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="fixed inset-0 flex flex-col items-center justify-center px-8"
      style={{ backgroundColor: '#0B1824' }}
    >
      <img
        src="/smarterhoops-logo.png"
        alt="SmarterHoops"
        className="w-44 max-w-[55vw] mb-12 select-none pointer-events-none"
        draggable="false"
      />

      <button
        type="button"
        onClick={handleApple}
        disabled={busy}
        data-testid="login-apple-button"
        className="w-full max-w-xs h-12 rounded-full bg-white text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[.98] transition-transform"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif' }}
      >
        {/* Apple logo (system glyph fallback) */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M16.365 1.43c0 1.14-.46 2.23-1.21 3.05-.81.88-2.17 1.55-3.31 1.46-.13-1.13.43-2.27 1.18-3.06.83-.88 2.27-1.55 3.34-1.45zM20.5 17.27c-.41.95-.6 1.37-1.13 2.21-.74 1.16-1.78 2.6-3.07 2.62-1.15.02-1.45-.75-3.01-.74-1.56.01-1.89.76-3.04.74-1.29-.02-2.27-1.32-3.01-2.48-2.07-3.25-2.29-7.06-1.01-9.09.91-1.43 2.34-2.27 3.69-2.27 1.37 0 2.24.75 3.37.75 1.1 0 1.77-.75 3.36-.75 1.2 0 2.48.65 3.4 1.78-2.99 1.64-2.51 5.92.45 7.23z"/>
        </svg>
        <span>{busy ? 'Signing in…' : 'Sign in with Apple'}</span>
      </button>

      <p className="text-slate-400 text-xs mt-8 max-w-xs text-center leading-snug">
        By continuing you agree to our{' '}
        <a href="/privacy" className="underline">Privacy Policy</a>.
      </p>
    </div>
  );
}
